import React from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, Loader2, ImageOff } from "lucide-react";
import { buildProductUrl } from "../../../../utils/navigation";
import { getSupabaseProductImageUrl } from "../../../../utils/imageUtils";

interface ProductCardProps {
  product: any;
  adding?: boolean;
  onAddToCart?: () => void;
}

export const ProductListCard: React.FC<ProductCardProps> = ({
  product,
  adding = false,
  onAddToCart,
}) => {
  const navigate = useNavigate();
  const [imgLoaded, setImgLoaded] = React.useState(false);
  const [imgError, setImgError] = React.useState(false);

  const images = product.images || [];
  const lastImageEntry = images[images.length - 1];
  const lastImage =
    typeof lastImageEntry === "string"
      ? lastImageEntry
      : (lastImageEntry as any)?.path;
  const imageUrl = getSupabaseProductImageUrl(lastImage);

  const displayPrice = product.price ?? 0;
  const originalPrice = product.original_price ?? product.price;
  const saleRate = product.sale_rate ?? product.discount ?? 0;
  const isOnSale = product.on_sale;
  const outOfStock =
    product.in_stock === false ||
    (product.in_stock === undefined &&
      !product.is_unlimited_stock &&
      (product.quantity ?? 0) <= 0);
  const hasVariants = !!product.has_variants;
  const ctaLabel = adding
    ? "Adding…"
    : outOfStock
      ? "Out of Stock"
      : hasVariants
        ? "Select options"
        : "Add to Cart";

  return (
    <div
      className="group bg-section-card-bg rounded-2xl border border-section-card-border shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 overflow-hidden cursor-pointer"
      onClick={() => navigate(buildProductUrl(product.id, product.name, product.brand_name))}
    >
      <div className="flex flex-col sm:flex-row sm:h-48">
        {/* Image */}
        <div className="relative w-full h-48 sm:w-56 sm:h-full shrink-0 bg-section-surface overflow-hidden">
          {/* Badges */}
          <div className="absolute top-2.5 left-2.5 z-10 flex flex-col gap-1.5">
            {isOnSale && saleRate > 0 && (
              <span className="bg-red-500 text-white text-[11px] font-bold px-2 py-0.5 rounded-md shadow-sm">
                -{saleRate}%
              </span>
            )}
            {product.is_new && !isOnSale && (
              <span className="bg-primary text-primary-foreground text-[11px] font-bold px-2 py-0.5 rounded-md shadow-sm">
                NEW
              </span>
            )}
          </div>

          {!imgLoaded && !imgError && (
            <div className="absolute inset-0 bg-section-surface animate-pulse" />
          )}

          {imgError ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-section-muted">
              <ImageOff className="w-8 h-8" />
              <span className="text-[10px] uppercase tracking-widest">No image</span>
            </div>
          ) : (
            <img
              src={imageUrl || product.image || "/assets/images/no-image.png"}
              alt={product.name ? `${product.name} product image` : "Product image"}
              className={`w-full h-full object-contain p-4 transition-all duration-500 group-hover:scale-110 ${imgLoaded ? "opacity-100" : "opacity-0"}`}
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgError(true)}
            />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 p-4 sm:p-5 flex flex-col justify-between min-w-0 gap-3">
          <div className="flex flex-col gap-1">
            {product.brand_name && (
              <p className="text-[10px] text-section-muted uppercase tracking-widest truncate">
                {product.brand_name}
              </p>
            )}

            <h3 className="text-sm sm:text-base font-medium text-section-text line-clamp-2 leading-snug group-hover:text-accent transition-colors">
              {product.name}
            </h3>

            {product.description && (
              <p className="text-sm text-section-muted line-clamp-1 leading-relaxed mt-1">
                {product.description}
              </p>
            )}
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            {/* Price */}
            <div className="flex items-baseline gap-2">
              <span className="text-lg sm:text-xl font-bold text-section-text">
                ${displayPrice.toFixed(2)}
              </span>
              {isOnSale && originalPrice && originalPrice > displayPrice && (
                <span className="text-xs text-section-muted line-through">
                  ${originalPrice.toFixed(2)}
                </span>
              )}
            </div>

            {/* Add to Cart */}
            <button
              className="w-full sm:w-auto bg-primary hover:bg-primary-dark text-primary-foreground px-4 py-2 rounded-lg flex items-center justify-center gap-2 font-semibold text-sm shadow-sm hover:shadow-md transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={(e) => {
                e.stopPropagation();
                onAddToCart?.();
              }}
              disabled={adding || outOfStock}
              aria-label="Add to cart"
            >
              {adding
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <ShoppingCart className="w-4 h-4" />
              }
              <span>{ctaLabel}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
